import React, {Component} from 'react';
import {injectIntl} from 'react-intl';
import {inject, observer} from 'mobx-react';
import {graphql} from '@apollo/client/react/hoc/graphql';
import {mapValues, pick} from 'lodash';

//helpers
import Field from '../Field';

import {FormattedMessage} from '../FormattedComponents';

import {bindField} from '../../utils/input-utils';

//messages
import messages from './messages';

import {UpdatePassword} from '../../api/user/mutations';
import {updatePasswordOptions} from '../../api/user/mutation-options';

//errors
import ErrorCodes from '../../login-errors';
import {posthogCapture} from '../../utils/posthog-utils';
import {posthogEvents} from '../../posthog-events';

import {
  ChangePasswordForm,
  Title,
  StyledText,
  StyledIcon,
  StyledButton,
  StyledErrorWrapper,
  StyledErrorIcon,
  ErrorNotification,
  getHeight,
  StyledButtonLink,
  StyledLink
} from './styles';

@inject('store')
@graphql(UpdatePassword, updatePasswordOptions)
@observer
class ChangePasswordComponent extends Component {
  componentDidMount() {
    const {
      intl: {formatMessage},
      store: {changePasswordPage}
    } = this.props;

    const translations = mapValues(pick(messages, ['changedPassword', 'canNotChangePassword']), message =>
      formatMessage(message)
    );

    changePasswordPage.setTranslations(translations);
  }

  changePassword = event => {
    event.preventDefault();
    posthogCapture(posthogEvents.attemptChangePassword);
    const {store, updatePasswordMutation} = this.props;
    const {changePasswordPage: page} = store;
    const {validatePassword} = page;
    validatePassword(updatePasswordMutation, store);
  };

  render() {
    const {
      intl: {formatMessage},
      store,
      isMobile
    } = this.props;
    const {changePasswordPage, app} = store;
    const {views} = app;
    const {
      form,
      passwordsMatch,
      isValidNewPassword,
      showChangePasswordForm,
      errorCode,
      setIsInputFocused,
      isInputFocused
    } = changePasswordPage;
    const inputStyles = {Input: getHeight()};
    const resetPasswordLink = `${window.location.origin}${views.requestResetPassword.path}`;

    return (
      <ChangePasswordForm isMobile={isMobile}>
        <Title isMobile={isMobile}>{formatMessage(messages.title)}</Title>
        {showChangePasswordForm && (
          <React.Fragment>
            <Field
              placeholder={formatMessage(messages.oldPassword)}
              id="oldPassword"
              type="password"
              form={form}
              field={'oldPassword'}
              hideError
              withPasswordToggle
              {...bindField(form, 'oldPassword')}
              styles={inputStyles}
            />
            {this.renderError() && (
              <StyledErrorWrapper>
                <StyledErrorIcon name={'warning'} />
                <ErrorNotification>{this.renderError()}</ErrorNotification>
              </StyledErrorWrapper>
            )}
            <StyledText alignText={'right'} onClick={() => posthogCapture(posthogEvents.selectForgotPassword)}>
              <StyledButtonLink href={resetPasswordLink} target="_blank">
                {formatMessage(messages.forgotYourPassword)}
              </StyledButtonLink>
            </StyledText>
            <Field
              placeholder={formatMessage(messages.newPassword)}
              id="newPassword"
              type="password"
              form={form}
              field={'newPassword'}
              hideError
              {...bindField(form, 'newPassword')}
              withPasswordToggle
              withPasswordValidation
              onFocus={() => setIsInputFocused(true)}
              onBlur={() => setIsInputFocused(false)}
              isInputFocused={isInputFocused}
              styles={inputStyles}
            />
            {!isValidNewPassword && (
              <StyledErrorWrapper>
                <StyledErrorIcon name={'warning'} />
                <ErrorNotification>{formatMessage(messages.meetAllRequirements)}</ErrorNotification>
              </StyledErrorWrapper>
            )}

            <Field
              placeholder={formatMessage(messages.newPasswordConfirm)}
              id="newPasswordConfirm"
              type="password"
              form={form}
              field={'newPasswordConfirm'}
              hideError
              {...bindField(form, 'newPasswordConfirm')}
              withPasswordToggle
              styles={inputStyles}
            />

            {!passwordsMatch && (
              <StyledErrorWrapper>
                <StyledErrorIcon name={'warning'} />
                <ErrorNotification>{formatMessage(messages.passwordsDoNotMatch)}</ErrorNotification>
              </StyledErrorWrapper>
            )}
            <StyledButton isMobile={isMobile} disabled={!form.isValid} onClick={this.changePassword}>
              {formatMessage(messages.changePassword)}
            </StyledButton>
            {!isMobile && (
              <StyledText>
                <StyledButtonLink onClick={() => window.history.back()}>
                  <StyledIcon name={'arrow-left'} />
                  {formatMessage(messages.goBack)}
                </StyledButtonLink>
              </StyledText>
            )}
          </React.Fragment>
        )}

        {!showChangePasswordForm && errorCode === ErrorCodes.BLOCKED && (
          <React.Fragment>
            {this.renderError() && (
              <StyledErrorWrapper>
                <StyledErrorIcon name={'warning'} />
                <ErrorNotification>{this.renderError()}</ErrorNotification>
              </StyledErrorWrapper>
            )}
          </React.Fragment>
        )}
      </ChangePasswordForm>
    );
  }
  renderError() {
    const {store} = this.props;
    const {changePasswordPage, app} = store;
    const {views} = app;
    const {errorCode, errorMessage, setHasChangePasswordError} = changePasswordPage;
    const resetPasswordLink = window.location.origin + views.requestResetPassword.path;

    if (!errorCode && !errorMessage) {
      return null;
    }

    const renderedErrorConfig = [
      {
        shouldRender: errorCode === 'invalid_password' && errorMessage === ErrorCodes.INVALID_PASSWORD,
        message: messages.wrongCurrent
      },
      {
        shouldRender: errorCode === ErrorCodes.FIFTH_ATTEMPT && errorMessage === ErrorCodes.INVALID_PASSWORD,
        message: messages.wrongCurrent4thTime,
        values: {
          passwordResetLink: (
            <StyledLink href={resetPasswordLink} target="_blank">
              <FormattedMessage {...messages.passwordResetLink} />
            </StyledLink>
          )
        }
      },
      {
        shouldRender: errorCode === ErrorCodes.BLOCKED && errorMessage === ErrorCodes.INVALID_PASSWORD,
        message: messages.wrongCurrent10thTime,
        values: {
          passwordResetLink: (
            <StyledLink href={resetPasswordLink} target="_blank">
              <FormattedMessage {...messages.passwordResetLink} />
            </StyledLink>
          )
        }
      }
    ];
    const renderedError = renderedErrorConfig.find(element => element.shouldRender);
    if (renderedError) {
      setHasChangePasswordError(true);
      return <FormattedMessage {...renderedError.message} values={renderedError.values} />;
    } else {
      setHasChangePasswordError(false);
    }
  }
}

export default injectIntl(ChangePasswordComponent);
